import { AppData } from '@lightningjs/sdk';
import { getCommonLanguageCode } from 'support/playerUtils';
import AbstractPlayerEngine from 'components/pages/playback/player-engines/AbstractPlayerEngine';
import { ValueOf } from 'types/index';
import { deepMerge } from 'support/appUtils';
import { DRM_KEY_SYSTEMS, DrmType } from 'services/drm/DRMService';

// Reverted dash 5.0 due to performance issues (see EL-1114 for dash 5.0 implementation)
import dashjs from 'dashjs';

type DashEvent = ValueOf<typeof dashjs.MediaPlayer.events>;

export default class DashPlayerEngine extends AbstractPlayerEngine<
  dashjs.TextTracksAddedEvent,
  [url: string]
> {
  private dash: dashjs.MediaPlayerClass;

  // Keep track of player listeners so we can clear them on player close
  private listeners: Partial<Record<DashEvent, (e: unknown) => void>> = {};

  protected videoElement: HTMLMediaElement;

  constructor(
    videoElement: HTMLMediaElement,
    licenseUrl: string,
    isDynamicStream = false,
  ) {
    super();

    this.videoElement = videoElement;

    /*
    currently there's a bug when we have a dynamic stream (like a DRM EPG program) we don't fetch subsequent sections unless we set src='' (EL-459)
    what causes this is unknown, we have thoroughly confirmed that our Dash configuration should work using https://reference.dashif.org/dash.js/
    - the current assumption of the issue is that there is a problem with our environment/strange interaction with our packages that is causing this
    - our guess as to why this fix works is that when we set src='' it throws an error thats caught and handled by Dash, when Dash handles this error it somehow
      fixes the dynamic stream issue (this fix also works if we set src to something invalid like src='error'). Further investigation of this will be required

    This logic causes an error to be reported to Conviva (EL-566), so we add this if condition to limit this false error to only DRM EPG programs
    */
    if (isDynamicStream) {
      videoElement.setAttribute('src', '');
    }

    const drmType = AppData?.device.getDrmType() ?? DrmType.WIDEVINE;

    const protectionData = {
      [DRM_KEY_SYSTEMS[drmType]]: {
        serverURL: licenseUrl,
        priority: 0,
      },
    };

    this.dash = dashjs.MediaPlayer().create();

    this.dash.extend(
      'RequestModifier',
      () => {
        return {
          modifyRequestHeader: (
            xhr: XMLHttpRequest,
            options?: { url?: string },
          ) => {
            this.augmentXhrRequest(xhr, options?.url);
            return xhr;
          },
        };
      },
      true,
    );

    this.dash.initialize();

    const settings = this.getDefaultSettings(drmType);
    this.dash.updateSettings(settings);

    const audio = AppData?.storageService.audio.get();
    if (audio) {
      this.dash.setInitialMediaSettingsFor('audio', { lang: audio });
    }
    this.dash.attachView(videoElement);
    this.dash.setAutoPlay(true);
    this.dash.setProtectionData(protectionData);

    this.addTextTracksAddedListener();
  }

  override destroy() {
    super.destroy();

    this.clearListeners();
    this.dash.reset();
    this.dash.destroy();
  }

  override async load(startTime: number | undefined, url: string) {
    this.dash.attachSource(url, startTime);
  }

  override onAdaption(handleAdaption: (bitrate: number) => void): void {
    this.addAdaptionListener(handleAdaption);
  }

  override onError(
    handleError: (isFatal: boolean, errorMessage: string) => void,
  ): void {
    this.addErrorListener(handleError);
  }

  override getAudioOptions(): string[] {
    const audioTracks: string[] = [];
    this.dash.getTracksFor('audio').forEach(track => {
      if (track.lang) audioTracks.push(track.lang);
    });
    return audioTracks;
  }

  override setAudioTrack(lang: string): void {
    const audioTrack = this.dash
      .getTracksFor('audio')
      .find(track => track.lang === lang);
    if (audioTrack) this.dash.setCurrentTrack(audioTrack);
  }

  override enableCc(lang: string) {
    if (super.enableCc(lang)) return;

    if (lang) {
      const trackIndex = this.getCcIndex(lang);
      if (trackIndex >= 0) {
        this.dash.setTextTrack(trackIndex);
        this.dash.enableText(true);
      }
    } else {
      this.dash.enableText(false);
    }
  }

  override disableCc() {
    if (super.disableCc()) return;

    this.dash.enableText(false);
  }

  override extractSubtitleOptions(
    addedTracksEvent: dashjs.TextTracksAddedEvent,
  ) {
    const languages = new Set<string>();
    addedTracksEvent.tracks.forEach(trackInfo => {
      const { kind, lang } = trackInfo;
      if (lang && (kind === 'subtitles' || kind === 'captions'))
        languages.add(getCommonLanguageCode(lang));
    });

    return Array.from(languages);
  }

  override getPlayerMedia(): HTMLMediaElement | null {
    return this.dash.getVideoElement();
  }

  private getDefaultSettings(drmType: DrmType): dashjs.MediaPlayerSettingClass {
    const isLowEndPlayer = AppData!.device.isLowEndPlayerPerformance();
    let settings: dashjs.MediaPlayerSettingClass = {
      // Log level 5 is `DEBUG`, 0 is `NONE`
      debug: { logLevel: AppData?.debug ? 5 : 0 },
      streaming: {
        text: {
          defaultEnabled: false,
        },
        abr: {
          limitBitrateByPortal: true, // default is false
        },
        // This resolves playback error when using PlayReady
        ...(drmType === DrmType.PLAYREADY
          ? {
              protection: {
                ignoreEmeEncryptedEvent: true,
              },
            }
          : {}),
      },
    };

    if (AppData?.device.isLowPerformanceModel()) {
      const platformSettings: dashjs.MediaPlayerSettingClass = {
        streaming: {
          abr: {
            usePixelRatioInLimitBitrateByPortal: isLowEndPlayer,
            maxBitrate: {
              video: 2000,
              audio: -1,
            },
            initialBitrate: {
              video: 800,
              audio: -1,
            },
          },
          buffer: {
            stableBufferTime: 25, // default is 12
            fastSwitchEnabled: isLowEndPlayer,
          },
          gaps: {
            enableStallFix: true,
          },
        },
      };
      settings = deepMerge(settings, platformSettings);
    }

    return settings;
  }

  private addTextTracksAddedListener() {
    const listener = dashjs.MediaPlayer.events.TEXT_TRACKS_ADDED;
    const cb = this.updateSubtitles.bind(this);

    this.addListener(listener, cb);
  }

  private addAdaptionListener(handleAdaption: (bitrate: number) => void) {
    const listener = dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED;
    const cb = (event: dashjs.QualityChangeRenderedEvent) => {
      if (event.mediaType !== 'video') return;

      const bitRateIndex = event.newQuality;
      const bitRates = this.dash.getBitrateInfoListFor(event.mediaType);

      if (bitRates[bitRateIndex] !== undefined) {
        const bitRate = bitRates[bitRateIndex]!.bitrate / 1000;
        this.currentBitrate = bitRate;

        handleAdaption(bitRate);
      }
    };

    this.addListener(listener, cb);
  }

  private addErrorListener(
    handleError: (isFatal: boolean, errorMessage: string) => void,
  ) {
    const listener = dashjs.MediaPlayer.events.ERROR;
    const cb = (event: dashjs.ErrorEvent) => {
      const { error } = event;
      if (typeof error === 'string') {
        handleError(true, error);
      } else {
        const { code, message } = error;
        handleError(true, `Error ${code}: ${message}`);
      }
    };

    this.addListener(listener, cb);
  }

  private addListener(listener: DashEvent, cb: (e: any) => void) {
    this.removeListener(listener);

    this.dash.on(listener, cb);
    this.listeners[listener] = cb;
  }

  private removeListener(listener: DashEvent) {
    const cb = this.listeners[listener];
    if (!cb) return;

    this.dash.off(listener, cb);
    delete this.listeners[listener];
  }

  private clearListeners() {
    Object.keys(this.listeners).forEach(event =>
      this.removeListener(event as DashEvent),
    );
  }
}
